import React, { useEffect, useState } from "react";
import {
  Star,
  CheckCircle,
  Clock,
  AlertTriangle,
  RefreshCw,
  ShieldAlert,
} from "lucide-react";
import Layout, { Container } from "../components/layout";
import { apiurl } from "../config/config";

const AgentPerformanceList = () => {
  const [agents, setAgents] = useState([]);

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    const res = await fetch(`${apiurl}/api/cst/agents-performance`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        role: "agent",
        departments: ["customer-support", "logistics", "quality", "warehouse"],
      }),
    });

    const data = await res.json();
    setAgents(data.users || []);
  };

  return (
    <Layout>
      <Container overflow={"overflow-scroll"}>
        <div className="p-6 bg-gray-50 min-h-screen">
          <h1 className="text-2xl font-semibold mb-6">
            Agent Performance Dashboard
          </h1>

          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
            {agents.map((agent) => {
              const p = agent.performance;
              const initials =
                agent.firstName?.[0] + agent.lastName?.[0];

              return (
                <div
                  key={agent._id}
                  className="bg-white rounded-2xl p-5 shadow-sm hover:shadow-xl transition-all border"
                >
                  {/* HEADER */}
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-3">
                      {/* Avatar */}
                      <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center font-semibold text-indigo-600">
                        {initials}
                      </div>

                      <div>
                        <h2 className="font-semibold text-lg capitalize">
                          {agent.firstName} {agent.lastName}
                        </h2>

                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span>{agent.role}</span>

                          {/* Login Restriction */}
                          {!agent.loginControl?.isLoginAllowed && (
                            <span className="flex items-center gap-1 text-red-500">
                              <ShieldAlert className="w-3 h-3" />
                              Restricted
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* ⭐ Rating */}
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                      <span className="font-medium text-sm">
                        {p.rating.avg || 0}
                      </span>
                    </div>
                  </div>

                  {/* STATS */}
                  <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                    <Stat
                      icon={<CheckCircle className="text-green-500 w-4" />}
                      label="Resolved"
                      value={p.resolvedTickets}
                    />
                    <Stat
                      icon={<Clock className="text-blue-500 w-4" />}
                      label="Total"
                      value={p.totalTickets}
                    />
                    <Stat
                      icon={<RefreshCw className="text-orange-500 w-4" />}
                      label="Reopened"
                      value={p.reopenedTickets}
                    />
                    <Stat
                      icon={<AlertTriangle className="text-red-500 w-4" />}
                      label="SLA Breach"
                      value={p.slaBreached}
                    />
                  </div>

                  {/* RESOLUTION BAR */}
                  <div className="mb-4">
                    <div className="flex justify-between text-xs mb-1">
                      <span>Resolution Rate</span>
                      <span>{p.resolutionRate}%</span>
                    </div>
                    <div className="w-full bg-gray-200 h-2 rounded-full">
                      <div
                        className="bg-green-500 h-2 rounded-full transition-all"
                        style={{ width: `${p.resolutionRate}%` }}
                      />
                    </div>
                  </div>

                  {/* ⭐ RATING SECTION */}
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 mb-2">
                      Customer Feedback ({p.rating.totalRatedTickets})
                    </p>

                    {p.rating.totalRatedTickets === 0 ? (
                      <div className="text-xs text-gray-400 italic">
                        No ratings yet
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {[5, 4, 3, 2, 1].map((star) => {
                          const count =
                            p.rating.breakdown[star] || 0;
                          const percent =
                            p.rating.totalRatedTickets > 0
                              ? (count /
                                  p.rating.totalRatedTickets) *
                                100
                              : 0;

                          return (
                            <div
                              key={star}
                              className="flex items-center gap-2 text-xs"
                            >
                              <span className="w-4">{star}</span>

                              <div className="flex-1 bg-gray-200 h-1.5 rounded">
                                <div
                                  className="bg-yellow-400 h-1.5 rounded"
                                  style={{ width: `${percent}%` }}
                                />
                              </div>

                              <span className="w-6 text-right text-gray-500">
                                {count}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* FOOTER */}
                  <div className="flex justify-between text-xs text-gray-500 border-t pt-3">
                    <span>
                      Avg Time: {p.avgResolutionTimeMinutes} min
                    </span>
                    <span>Closed: {p.closedTickets}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Container>
    </Layout>
  );
};

const Stat = ({ icon, label, value }) => (
  <div className="flex items-center gap-2">
    {icon}
    <div>
      <p className="text-gray-500 text-xs">{label}</p>
      <p className="font-semibold">{value}</p>
    </div>
  </div>
);

export default AgentPerformanceList;